import {
  CODE_FONT_KEY,
  isSupportedFontKey,
  type PdfFontKey,
} from "@/src/lib/pdf/fonts";
import {
  defaultPdfOptions,
  isPdfCodeTheme,
  isPdfDiagramTheme,
  isPdfTheme,
  type MarginPreset,
  type Orientation,
  type PaperSize,
  type PdfCodeTheme,
  type PdfDiagramTheme,
  type PdfOptions,
  type PdfTheme,
} from "@/src/lib/pdf/print-document";

export type PersistedPreferences = {
  version: number;
  pdfOptions: Partial<PdfOptions>;
  editorFontFamily: PdfFontKey;
  editorFontSize: number;
  splitRatio: number;
};

/** Surfaces that own a preferences namespace (embeds + dedicated editor). */
export const editorPreferencesSources = [
  "home",
  "resume",
  "readme",
  "chatgpt",
  "gemini",
  "mermaid",
  "editor",
] as const;

export type EditorPreferencesSource =
  (typeof editorPreferencesSources)[number];

/** Legacy unscoped key — read as fallback for `editor` only. */
export const STORAGE_KEY = "markdown2pdf-preferences";
export const STORAGE_VERSION = 1;
export const DEFAULT_SPLIT_RATIO = 0.5;
export const DEFAULT_EDITOR_FONT_FAMILY = CODE_FONT_KEY;
export const DEFAULT_EDITOR_FONT_SIZE = 12;

const paperSizeValues: PaperSize[] = ["a4", "letter"];
const orientationValues: Orientation[] = ["portrait", "landscape"];
const marginPresetValues: MarginPreset[] = ["narrow", "normal", "wide"];
const minFontSize = 8;
const maxFontSize = 24;

export function isEditorPreferencesSource(
  value: unknown,
): value is EditorPreferencesSource {
  return (
    typeof value === "string" &&
    (editorPreferencesSources as readonly string[]).includes(value)
  );
}

export function preferencesStorageKey(source: EditorPreferencesSource): string {
  return `${STORAGE_KEY}:${source}`;
}

function isPaperSize(value: unknown): value is PaperSize {
  return typeof value === "string" && paperSizeValues.includes(value as PaperSize);
}

function isOrientation(value: unknown): value is Orientation {
  return typeof value === "string" && orientationValues.includes(value as Orientation);
}

function isMarginPreset(value: unknown): value is MarginPreset {
  return typeof value === "string" && marginPresetValues.includes(value as MarginPreset);
}

function isValidFontSize(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= minFontSize &&
    value <= maxFontSize
  );
}

function isValidSplitRatio(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1;
}

export function parsePartialPdfOptions(value: unknown): Partial<PdfOptions> {
  if (typeof value !== "object" || value === null) {
    return {};
  }

  const raw = value as Record<string, unknown>;
  const parsed: Partial<PdfOptions> = {};

  if (isPaperSize(raw.paperSize)) {
    parsed.paperSize = raw.paperSize;
  }

  if (isOrientation(raw.orientation)) {
    parsed.orientation = raw.orientation;
  }

  if (isMarginPreset(raw.margins)) {
    parsed.margins = raw.margins;
  }

  if (isPdfTheme(raw.theme)) {
    parsed.theme = raw.theme;
  }

  if (isPdfCodeTheme(raw.codeTheme)) {
    parsed.codeTheme = raw.codeTheme;
  }

  if (isPdfDiagramTheme(raw.diagramTheme)) {
    parsed.diagramTheme = raw.diagramTheme;
  }

  if (typeof raw.pageNumbers === "boolean") {
    parsed.pageNumbers = raw.pageNumbers;
  }

  if (isSupportedFontKey(raw.fontFamily)) {
    parsed.fontFamily = raw.fontFamily;
  }

  if (isValidFontSize(raw.fontSize)) {
    parsed.fontSize = raw.fontSize;
  }

  if (isSupportedFontKey(raw.diagramFontFamily)) {
    parsed.diagramFontFamily = raw.diagramFontFamily;
  }

  if (isValidFontSize(raw.diagramFontSize)) {
    parsed.diagramFontSize = raw.diagramFontSize;
  }

  if (isSupportedFontKey(raw.codeFontFamily)) {
    parsed.codeFontFamily = raw.codeFontFamily;
  }

  if (isValidFontSize(raw.codeFontSize)) {
    parsed.codeFontSize = raw.codeFontSize;
  }

  return parsed;
}

function parsePersistedPreferences(value: unknown): PersistedPreferences | null {
  if (typeof value !== "object" || value === null) {
    return null;
  }

  const raw = value as Record<string, unknown>;

  if (raw.version !== STORAGE_VERSION) {
    return null;
  }

  const splitRatio = isValidSplitRatio(raw.splitRatio)
    ? raw.splitRatio
    : DEFAULT_SPLIT_RATIO;
  const pdfOptions = parsePartialPdfOptions(raw.pdfOptions);

  // Seed editor fonts from stored PDF fonts when editor prefs are absent so
  // existing users keep one shared look until they diverge.
  const editorFontFamily = isSupportedFontKey(raw.editorFontFamily)
    ? raw.editorFontFamily
    : (pdfOptions.fontFamily ?? DEFAULT_EDITOR_FONT_FAMILY);
  const editorFontSize = isValidFontSize(raw.editorFontSize)
    ? raw.editorFontSize
    : (pdfOptions.fontSize ?? DEFAULT_EDITOR_FONT_SIZE);

  return {
    version: STORAGE_VERSION,
    pdfOptions,
    editorFontFamily,
    editorFontSize,
    splitRatio,
  };
}

function readPreferencesFromKey(key: string): PersistedPreferences | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return null;
    }
    return parsePersistedPreferences(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function loadPersistedPreferences(
  source: EditorPreferencesSource = "editor",
): PersistedPreferences | null {
  if (typeof window === "undefined") {
    return null;
  }

  const scoped = readPreferencesFromKey(preferencesStorageKey(source));
  if (scoped) {
    return scoped;
  }

  // Migrate legacy unscoped prefs into the dedicated editor namespace.
  if (source === "editor") {
    return readPreferencesFromKey(STORAGE_KEY);
  }

  return null;
}

export function savePersistedPreferences(
  preferences: PersistedPreferences,
  source: EditorPreferencesSource = "editor",
): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(
      preferencesStorageKey(source),
      JSON.stringify(preferences),
    );
  } catch {
    // Storage may be disabled or full; silently ignore so the app keeps working.
  }
}

export function buildPersistedPreferences(
  pdfOptions: PdfOptions,
  splitRatio: number,
  editorFontFamily: PdfFontKey = DEFAULT_EDITOR_FONT_FAMILY,
  editorFontSize: number = DEFAULT_EDITOR_FONT_SIZE,
): PersistedPreferences {
  return {
    version: STORAGE_VERSION,
    pdfOptions: {
      paperSize: pdfOptions.paperSize,
      orientation: pdfOptions.orientation,
      margins: pdfOptions.margins,
      theme: pdfOptions.theme,
      codeTheme: pdfOptions.codeTheme,
      diagramTheme: pdfOptions.diagramTheme,
      pageNumbers: pdfOptions.pageNumbers,
      fontFamily: pdfOptions.fontFamily,
      fontSize: pdfOptions.fontSize,
      diagramFontFamily: pdfOptions.diagramFontFamily,
      diagramFontSize: pdfOptions.diagramFontSize,
      codeFontFamily: pdfOptions.codeFontFamily,
      codeFontSize: pdfOptions.codeFontSize,
    },
    editorFontFamily,
    editorFontSize,
    splitRatio,
  };
}

export function mergePdfOptionsWithDefaults(
  partial: Partial<PdfOptions>,
): PdfOptions {
  return {
    paperSize: partial.paperSize ?? defaultPdfOptions.paperSize,
    orientation: partial.orientation ?? defaultPdfOptions.orientation,
    margins: partial.margins ?? defaultPdfOptions.margins,
    theme: partial.theme ?? defaultPdfOptions.theme,
    codeTheme: partial.codeTheme ?? defaultPdfOptions.codeTheme,
    diagramTheme: partial.diagramTheme ?? defaultPdfOptions.diagramTheme,
    pageNumbers: partial.pageNumbers ?? defaultPdfOptions.pageNumbers,
    fontFamily: partial.fontFamily ?? defaultPdfOptions.fontFamily,
    fontSize: partial.fontSize ?? defaultPdfOptions.fontSize,
    // Older prefs had no diagram font; keep diagrams matching body until set.
    diagramFontFamily:
      partial.diagramFontFamily ??
      partial.fontFamily ??
      defaultPdfOptions.diagramFontFamily,
    diagramFontSize:
      partial.diagramFontSize ?? defaultPdfOptions.diagramFontSize,
    codeFontFamily:
      partial.codeFontFamily ?? defaultPdfOptions.codeFontFamily,
    codeFontSize: partial.codeFontSize ?? defaultPdfOptions.codeFontSize,
  };
}

export type { PdfCodeTheme, PdfDiagramTheme, PdfTheme };
